/**
 * Horizontal Ray — a SINGLE-click tool.
 *
 * This deliberately OVERRIDES klinecharts' built-in `horizontalRayLine`
 * (`registerOverlay` overwrites the registry by name). The built-in is
 * `totalStep: 3` — two points — and its template draws to `x = 0` (LEFT)
 * unless a second point happens to sit to the right of the first:
 *
 *     var coordinate = { x: 0, y: coordinates[0].y }
 *     if (isValid(coordinates[1]) && coordinates[0].x < coordinates[1].x)
 *         coordinate.x = bounding.width
 *
 * That made a one-click placement draw the ray BACKWARDS and leave it at
 * `currentStep = 2` (mid-draw), so the chart stayed armed. Conceptually a
 * horizontal ray is fully defined by one anchor — a price and where it starts —
 * and it extends right from there; requiring a second click only to state a
 * direction that never varies is busywork, and auto-placing that second point
 * would fabricate geometry the user never drew.
 *
 * So: one point, one click, `totalStep: 2`, always rightward.
 *
 * The name is kept as `horizontalRayLine` on purpose — it is the persisted key
 * (`SavedDrawing.name`), so renaming it would orphan every saved ray. Drawings
 * saved under the old two-point behaviour still load: extra points beyond the
 * first are simply ignored, and `points.length >= totalStep - 1` holds for both
 * one- and two-point records, so neither restores mid-draw.
 */
import { registerOverlay } from 'klinecharts'
import { isNum, type Coord, type OverlayFigure } from './overlayUtils'
import { DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'
import { buildLevelLabelFigure, levelLabelOf } from './horizontalLineOverlay'

/** Style the toolbar can override per drawing. */
export interface HorizontalRayStyle {
  color?: string
  size?: number
  style?: 'solid' | 'dashed'
}

/**
 * A level line from the anchor to the right edge of the pane.
 *
 * `width` is the pane's own width rather than a large constant: an off-screen
 * ray with a huge span still has geometry crossing the viewport, which is what
 * left dashed artefacts pinned to the chart edge for the range tools.
 */
export function buildHorizontalRayFigures(
  coordinates: Coord[],
  paneWidth: number,
  style: HorizontalRayStyle = {},
  label?: string,
): OverlayFigure[] {
  const anchor = coordinates[0]
  if (!isNum(anchor?.x) || !isNum(anchor?.y)) return []
  // Anchored off the right edge — nothing of the ray is visible.
  if (anchor.x > paneWidth) return []

  return [
    {
      key: 'horizontal_ray',
      type: 'line',
      attrs: {
        coordinates: [
          { x: anchor.x, y: anchor.y },
          { x: paneWidth, y: anchor.y },
        ],
      },
      styles: {
        ...(style.color ? { color: style.color } : {}),
        ...(style.size ? { size: style.size } : {}),
        ...(style.style === 'dashed' ? { style: 'dashed', dashedValue: [4, 4] } : {}),
      },
    },
    ...buildLevelLabelFigure(anchor.y, paneWidth, label, style.color),
  ]
}

/** Register the single-click horizontal ray. Idempotent. */
export const registerHorizontalRayOverlay = registerOnce(() => {
  registerOverlay({
    name: 'horizontalRayLine',
    totalStep: 2, // single click
    ...DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, overlay, bounding }) =>
      buildHorizontalRayFigures(
        coordinates as Coord[],
        (bounding as { width: number })?.width ?? 0,
        // Toolbar edits land on `styles.line` via overrideOverlayStyle; the
        // template has to READ them or the controls silently do nothing.
        ((overlay?.styles as { line?: HorizontalRayStyle })?.line ?? {}) as HorizontalRayStyle,
        levelLabelOf(overlay?.extendData),
      ),
    // Dragging the line moves the level; the ray stays horizontal.
    performEventPressedMove: ({ points, performPoint }) => {
      points[0].value = performPoint.value
    },
  } as OverlayTemplateParam)
})
